import { useMemo } from 'react';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  addChannelMember,
  archiveChannel,
  createChannel,
  createChannelMessage,
  createChannelMessageWithFiles,
  createDirectChannel,
  deleteChannelMessage,
  getChannelMembers,
  getChannelCreationMembers,
  getChannelMessages,
  getEligibleChannelMembers,
  getChannels,
  getChatPeople,
  getChannelAudiences,
  getChatPresence,
  getMentionGroups,
  getSavedMessages,
  markChannelRead,
  removeChannelMember,
  restoreChannel,
  searchChannelMessages,
  toggleMessagePin,
  toggleMessageReaction,
  toggleSavedMessage,
  updateChannel,
  updateChannelMember,
  updateChannelMessage,
  voteChannelPoll,
  type CreateChannelPayload,
  type CreateMessagePayload,
  type MessageSearchParams,
  type ChatPresence,
  type ChatUser,
  type ChannelMemberData,
  type PresenceStatus,
  type SavedMessagesPage,
  type ChannelData,
  type ChatAttachment,
  type ChannelMessageData,
} from '@/api/services/channels.service';
import { useMe } from '@/features/settings/hooks/useMe';

export const channelKeys = {
  all: ['channels'] as const,
  list: (archived = false) => ['channels', 'list', archived] as const,
  messagesRoot: (channelId: string) => ['channels', 'messages', channelId] as const,
  messages: (channelId: string, threadRootId?: string) =>
    ['channels', 'messages', channelId, threadRootId ?? 'timeline'] as const,
  members: (channelId: string) => ['channels', 'members', channelId] as const,
  people: ['channels', 'people'] as const,
  search: ['channels', 'search'] as const,
  presence: ['channels', 'presence'] as const,
  saved: ['channels', 'saved'] as const,
  /** Matches the key PinnedMessagesModal already reads. */
  pins: (channelId: string) => ['channels', 'pins', channelId] as const,
  eligibleMembers: (channelId: string) => ['channels', 'eligible-members', channelId] as const,
  creationMembers: (companyId: string) => ['channels', 'creation-members', companyId] as const,
};

export function useChannels(archived = false, enabled = true) {
  return useQuery({
    queryKey: channelKeys.list(archived),
    queryFn: () => getChannels(archived),
    staleTime: 30_000,
    enabled,
  });
}

export function useChannelMessages(channelId: string | null, threadRootId?: string) {
  return useQuery({
    queryKey: channelKeys.messages(channelId ?? '', threadRootId),
    queryFn: () => getChannelMessages(channelId!, { threadRootId }),
    enabled: Boolean(channelId),
    staleTime: 0,
  });
}

/**
 * A window of timeline messages centred on `sequence`, for jumping to a search
 * result older than the loaded timeline. It shares the `messagesRoot` prefix so
 * realtime edits, deletes and reactions update it like the timeline.
 */
export function useChannelMessagesAround(channelId: string | null, sequence: number | null) {
  return useQuery({
    queryKey: [...channelKeys.messagesRoot(channelId ?? ''), 'around', sequence] as const,
    queryFn: () => getChannelMessages(channelId!, { around: sequence! }),
    enabled: Boolean(channelId) && sequence != null,
    staleTime: 30_000,
  });
}

export function useChannelMembers(channelId: string | null) {
  return useQuery({
    queryKey: channelKeys.members(channelId ?? ''),
    queryFn: () => getChannelMembers(channelId!),
    enabled: Boolean(channelId),
    staleTime: 60_000,
  });
}

export function useEligibleChannelMembers(channelId: string | null, enabled = true) {
  return useQuery({
    queryKey: channelKeys.eligibleMembers(channelId ?? ''),
    queryFn: () => getEligibleChannelMembers(channelId!),
    enabled: enabled && Boolean(channelId),
    staleTime: 60_000,
  });
}

export function useChannelCreationMembers(companyId: string | null, enabled = true) {
  return useQuery({
    queryKey: channelKeys.creationMembers(companyId ?? ''),
    queryFn: () => getChannelCreationMembers(companyId!),
    enabled: enabled && Boolean(companyId),
    staleTime: 60_000,
  });
}

/** Departments and teams of a company, for creating department/team channels. */
export function useChannelAudiences(companyId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['channels', 'audiences', companyId ?? ''] as const,
    queryFn: () => getChannelAudiences(companyId!),
    enabled: enabled && Boolean(companyId),
    staleTime: 60_000,
  });
}

/** Roles that can be @mentioned in this channel (none for DMs). */
export function useMentionGroups(channelId: string | null, enabled = true) {
  return useQuery({
    queryKey: [...channelKeys.members(channelId ?? ''), 'mention-groups'] as const,
    queryFn: () => getMentionGroups(channelId!),
    enabled: enabled && Boolean(channelId),
    staleTime: 60_000,
  });
}

/** Server-side message search; the API needs at least two characters. */
export function useMessageSearch(params: MessageSearchParams) {
  const enabled = params.q.trim().length >= 2;
  return useQuery({
    queryKey: [...channelKeys.search, params] as const,
    queryFn: () => searchChannelMessages({ ...params, q: params.q.trim() }),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Online status for the given users, keyed by user id. Live changes arrive as
 * `chat.presence` socket events (see `applyPresenceUpdate`); the interval is a
 * fallback for missed events.
 */
export function usePresence(userIds: string[], enabled = true): Map<string, PresenceStatus> {
  const ids = [...new Set(userIds.filter(Boolean))].sort();
  const { data } = useQuery({
    queryKey: [...channelKeys.presence, ids.join(',')] as const,
    queryFn: () => getChatPresence(ids),
    enabled: enabled && ids.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
  return useMemo(
    () => new Map((data ?? []).map((presence) => [presence.userId, presence.status])),
    [data]
  );
}

/** Patches every cached presence list that includes this user. */
export function applyPresenceUpdate(
  queryClient: ReturnType<typeof useQueryClient>,
  update: ChatPresence
): void {
  queryClient.setQueriesData<ChatPresence[]>({ queryKey: channelKeys.presence }, (current) =>
    current?.map((presence) =>
      presence.userId === update.userId ? { ...presence, status: update.status } : presence
    )
  );
}

/** People you can message. `search` is matched by the API, which only returns a capped page. */
export function useChatPeople(enabled = true, search = '') {
  const query = search.trim();
  return useQuery({
    queryKey: query ? [...channelKeys.people, query] : channelKeys.people,
    queryFn: () => getChatPeople(query || undefined),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateChannelPayload) => createChannel(payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelKeys.all }),
  });
}

export function useCreateDirectChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userIds, name }: { userIds: string[]; name?: string }) =>
      createDirectChannel(userIds, name),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: channelKeys.all }),
  });
}

export function useSendChannelMessage() {
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  return useMutation({
    mutationFn: ({
      channelId,
      payload,
      files,
    }: {
      channelId: string;
      payload: CreateMessagePayload;
      files?: File[];
    }) =>
      files && files.length > 0
        ? createChannelMessageWithFiles(channelId, payload, files)
        : createChannelMessage(channelId, payload),
    onMutate: async ({ channelId, payload, files = [] }) => {
      const clientMessageId = payload.clientMessageId ?? crypto.randomUUID();
      const temporaryId = `optimistic:${clientMessageId}`;
      const now = new Date().toISOString();
      const optimisticAttachments: ChatAttachment[] = files.map((file, index) => ({
        key: `optimistic:${clientMessageId}:${index}`,
        url: URL.createObjectURL(file),
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        ...(file.type.startsWith('audio/') && payload.voiceDurationSeconds
          ? { durationSeconds: payload.voiceDurationSeconds }
          : {}),
        ...(file.type.startsWith('audio/') && payload.voiceWaveform?.length
          ? { waveform: payload.voiceWaveform }
          : {}),
      }));
      const optimisticMessage: ChannelMessageData = {
        _id: temporaryId,
        channelId,
        senderId: {
          _id: me?._id ?? 'me',
          firstName: me?.firstName ?? 'You',
          lastName: me?.lastName ?? '',
          email: me?.email ?? '',
          ...(me?.avatar ? { avatar: me.avatar } : {}),
        },
        content: payload.content ?? '',
        sequence: Number.MAX_SAFE_INTEGER,
        ...(payload.threadRootId ? { threadRootId: payload.threadRootId } : {}),
        replyCount: 0,
        mentions: payload.mentions ?? [],
        attachments: optimisticAttachments,
        reactions: [],
        clientMessageId,
        deliveryState: 'sending',
        createdAt: now,
        updatedAt: now,
      };
      const messageKey = channelKeys.messages(channelId, payload.threadRootId);
      await queryClient.cancelQueries({ queryKey: messageKey });
      queryClient.setQueryData<ChannelMessageData[]>(messageKey, (current = []) => [
        ...current,
        optimisticMessage,
      ]);
      if (payload.threadRootId) {
        queryClient.setQueryData<ChannelMessageData[]>(channelKeys.messages(channelId), (current) =>
          current?.map((message) =>
            message._id === payload.threadRootId
              ? { ...message, replyCount: message.replyCount + 1 }
              : message
          )
        );
      }
      return {
        temporaryId,
        messageKey,
        threadRootId: payload.threadRootId,
        previewUrls: optimisticAttachments.map((attachment) => attachment.url),
      };
    },
    onError: (_error, variables, context) => {
      if (!context) return;
      context.previewUrls.forEach((url) => URL.revokeObjectURL(url));
      const isStillTemporary = queryClient
        .getQueryData<ChannelMessageData[]>(context.messageKey)
        ?.some((message) => message._id === context.temporaryId);
      if (!isStillTemporary) return;
      queryClient.setQueryData<ChannelMessageData[]>(context.messageKey, (current) =>
        current?.filter((message) => message._id !== context.temporaryId)
      );
      if (context.threadRootId) {
        queryClient.setQueryData<ChannelMessageData[]>(
          channelKeys.messages(variables.channelId),
          (current) =>
            current?.map((message) =>
              message._id === context.threadRootId
                ? { ...message, replyCount: Math.max(0, message.replyCount - 1) }
                : message
            )
        );
      }
    },
    onSuccess: (message, variables, context) => {
      if (!context) return;
      context.previewUrls.forEach((url) => URL.revokeObjectURL(url));
      queryClient.setQueryData<ChannelMessageData[]>(context.messageKey, (current = []) => {
        let replaced = false;
        const next = current.flatMap((item) => {
          const isTemporary = item._id === context.temporaryId;
          const isServerCopy = item._id === message._id;
          if (!isTemporary && !isServerCopy) return [item];
          if (replaced) return [];
          replaced = true;
          return [message];
        });
        return replaced ? next : [...next, message];
      });
      queryClient.setQueriesData<ChannelData[]>({ queryKey: ['channels', 'list'] }, (current) =>
        current?.map((channel) =>
          channel._id === variables.channelId
            ? {
                ...channel,
                lastMessageSequence: Math.max(channel.lastMessageSequence, message.sequence),
                lastMessageAt: message.createdAt,
              }
            : channel
        )
      );
    },
  });
}

export function useToggleReaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      messageId,
      emoji,
    }: {
      channelId: string;
      messageId: string;
      emoji: string;
    }) => toggleMessageReaction(channelId, messageId, emoji),
    onMutate: async ({ channelId, messageId, emoji }) => {
      const key = channelKeys.messagesRoot(channelId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ChannelMessageData[]>({ queryKey: key });
      queryClient.setQueriesData<ChannelMessageData[]>({ queryKey: key }, (current) =>
        current?.map((message) => {
          if (message._id !== messageId) return message;
          const existing = message.reactions.find((reaction) => reaction.emoji === emoji);
          const reactions = existing
            ? message.reactions
                .map((reaction) =>
                  reaction.emoji === emoji
                    ? {
                        ...reaction,
                        count: Math.max(0, reaction.count + (reaction.reacted ? -1 : 1)),
                        reacted: !reaction.reacted,
                      }
                    : reaction
                )
                .filter((reaction) => reaction.count > 0)
            : [...message.reactions, { emoji, count: 1, reacted: true }];
          return { ...message, reactions };
        })
      );
      return { previous };
    },
    // Socket delivery updates all connected clients.  Apply the HTTP response too,
    // so the initiating user sees the change even during a brief reconnect.
    onSuccess: (result, { channelId, messageId }) => {
      queryClient.setQueriesData<ChannelMessageData[]>(
        { queryKey: channelKeys.messagesRoot(channelId) },
        (current) =>
          current?.map((message) =>
            message._id === messageId ? { ...message, reactions: result.reactions } : message
          )
      );
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value));
    },
  });
}

/**
 * Optimistically patches one channel in the active sidebar list; returns the
 * previous list for rollback.
 */
async function patchActiveChannel(
  queryClient: ReturnType<typeof useQueryClient>,
  channelId: string,
  update: (channel: ChannelData) => ChannelData
): Promise<ChannelData[] | undefined> {
  await queryClient.cancelQueries({ queryKey: channelKeys.list(false) });
  const previous = queryClient.getQueryData<ChannelData[]>(channelKeys.list(false));
  queryClient.setQueryData<ChannelData[]>(channelKeys.list(false), (current) =>
    current?.map((channel) => (channel._id === channelId ? update(channel) : channel))
  );
  return previous;
}

/**
 * Optimistic: the "Pinned messages" list flips at once (when it is loaded).
 * Pass `message` so a newly pinned message can be shown before the refetch.
 */
export function useTogglePin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      messageId,
    }: {
      channelId: string;
      messageId: string;
      message?: ChannelMessageData;
    }) => toggleMessagePin(channelId, messageId),
    onMutate: async ({ channelId, messageId, message }) => {
      const key = channelKeys.pins(channelId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ChannelMessageData[]>(key);
      if (previous) {
        const isPinned = previous.some((item) => item._id === messageId);
        queryClient.setQueryData<ChannelMessageData[]>(
          key,
          isPinned
            ? previous.filter((item) => item._id !== messageId)
            : message
              ? [message, ...previous]
              : previous
        );
      }
      return { previous };
    },
    onError: (_error, { channelId }, context) => {
      if (context?.previous)
        queryClient.setQueryData(channelKeys.pins(channelId), context.previous);
    },
    onSettled: (_result, _error, { channelId }) =>
      queryClient.invalidateQueries({ queryKey: channelKeys.pins(channelId) }),
  });
}

/** The user's saved messages, newest save first, loaded a page at a time. */
export function useSavedMessages(enabled = true) {
  return useInfiniteQuery({
    queryKey: channelKeys.saved,
    queryFn: ({ pageParam }) => getSavedMessages(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Sets a message's `saved` flag everywhere it is cached (timelines, threads,
 * history windows, search results) and drops it from the saved list on
 * unsave. Shared by the optimistic toggle and the cross-tab socket event.
 */
export function applySavedChange(
  queryClient: ReturnType<typeof useQueryClient>,
  messageId: string,
  saved: boolean
): void {
  const mark = <T extends ChannelMessageData>(list: T[] | undefined) =>
    list?.map((message) => (message._id === messageId ? { ...message, saved } : message));
  queryClient.setQueriesData<ChannelMessageData[]>({ queryKey: ['channels', 'messages'] }, mark);
  queryClient.setQueriesData<ChannelMessageData[]>({ queryKey: channelKeys.search }, mark);
  if (!saved) {
    queryClient.setQueryData<InfiniteData<SavedMessagesPage>>(channelKeys.saved, (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.filter((item) => item._id !== messageId),
            })),
          }
        : current
    );
  }
}

/** Optimistic save/unsave; a failure flips the flag back. */
export function useToggleSaved() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      messageId,
    }: {
      channelId: string;
      messageId: string;
      saved: boolean;
    }) => toggleSavedMessage(channelId, messageId),
    onMutate: async ({ messageId, saved }) => {
      await queryClient.cancelQueries({ queryKey: channelKeys.saved });
      const previousSaved = queryClient.getQueryData<InfiniteData<SavedMessagesPage>>(
        channelKeys.saved
      );
      applySavedChange(queryClient, messageId, !saved);
      return { previousSaved };
    },
    onError: (_error, { messageId, saved }, context) => {
      applySavedChange(queryClient, messageId, saved);
      if (context?.previousSaved)
        queryClient.setQueryData(channelKeys.saved, context.previousSaved);
    },
    onSuccess: (result, { messageId }) => {
      // The server's answer wins, e.g. after a toggle from another tab.
      applySavedChange(queryClient, messageId, result.saved);
      if (result.saved) void queryClient.invalidateQueries({ queryKey: channelKeys.saved });
    },
  });
}

export function useEditChannelMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      messageId,
      content,
      mentions,
      mentionRoles,
    }: {
      channelId: string;
      messageId: string;
      content: string;
      /** Omit to keep the message's existing mentions. */
      mentions?: string[];
      mentionRoles?: string[];
    }) =>
      updateChannelMessage(channelId, messageId, {
        content,
        ...(mentions ? { mentions } : {}),
        ...(mentionRoles ? { mentionRoles } : {}),
      }),
    onMutate: async ({ channelId, messageId, content, mentions, mentionRoles }) => {
      const key = channelKeys.messagesRoot(channelId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ChannelMessageData[]>({ queryKey: key });
      const editedAt = new Date().toISOString();
      queryClient.setQueriesData<ChannelMessageData[]>({ queryKey: key }, (current) =>
        current?.map((message) =>
          message._id === messageId
            ? {
                ...message,
                content,
                editedAt,
                ...(mentions ? { mentions } : {}),
                ...(mentionRoles ? { mentionRoles } : {}),
              }
            : message
        )
      );
      return { previous };
    },
    onSuccess: (message, { channelId, messageId }) => {
      queryClient.setQueriesData<ChannelMessageData[]>(
        { queryKey: channelKeys.messagesRoot(channelId) },
        (current) => current?.map((item) => (item._id === messageId ? message : item))
      );
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value));
    },
  });
}

export function useVoteChannelPoll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      messageId,
      optionId,
    }: {
      channelId: string;
      messageId: string;
      optionId: string;
      optimisticContent: string;
    }) => voteChannelPoll(channelId, messageId, optionId),
    onMutate: async ({ channelId, messageId, optimisticContent }) => {
      const key = channelKeys.messagesRoot(channelId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ChannelMessageData[]>({ queryKey: key });
      queryClient.setQueriesData<ChannelMessageData[]>({ queryKey: key }, (current) =>
        current?.map((message) =>
          message._id === messageId ? { ...message, content: optimisticContent } : message
        )
      );
      return { previous };
    },
    onSuccess: (message, { channelId, messageId }) => {
      queryClient.setQueriesData<ChannelMessageData[]>(
        { queryKey: channelKeys.messagesRoot(channelId) },
        (current) => current?.map((item) => (item._id === messageId ? message : item))
      );
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value));
    },
  });
}

export function useDeleteChannelMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, messageId }: { channelId: string; messageId: string }) =>
      deleteChannelMessage(channelId, messageId),
    onMutate: async ({ channelId, messageId }) => {
      const key = channelKeys.messagesRoot(channelId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ChannelMessageData[]>({ queryKey: key });
      const deletedAt = new Date().toISOString();
      queryClient.setQueriesData<ChannelMessageData[]>({ queryKey: key }, (current) =>
        current?.map((message) =>
          message._id === messageId
            ? { ...message, content: '', attachments: [], deletedAt }
            : message
        )
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value));
    },
  });
}

export function useMarkChannelRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, sequence }: { channelId: string; sequence?: number }) =>
      markChannelRead(channelId, sequence),
    // Clear the badge as soon as the user reaches the latest message. The API
    // still remains the source of truth and the snapshot is restored if it
    // rejects the read receipt.
    onMutate: async ({ channelId, sequence }) => {
      const queryKey = ['channels', 'list'] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueriesData<ChannelData[]>({ queryKey });
      queryClient.setQueriesData<ChannelData[]>({ queryKey }, (current) =>
        current?.map((channel) => {
          if (channel._id !== channelId) return channel;
          const readThrough = sequence ?? channel.lastMessageSequence;
          return readThrough >= channel.lastMessageSequence
            ? { ...channel, unreadCount: 0 }
            : channel;
        })
      );
      return { previous };
    },
    // Marking a message as read must not invalidate the broad `['channels']`
    // prefix: that prefix also matches messages and members and caused a GET loop.
    onSuccess: (result, { channelId }) => {
      queryClient.setQueriesData<ChannelData[]>({ queryKey: ['channels', 'list'] }, (current) =>
        current?.map((channel) =>
          channel._id === channelId
            ? {
                ...channel,
                unreadCount: Math.max(0, channel.lastMessageSequence - result.lastReadSequence),
              }
            : channel
        )
      );
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value));
    },
  });
}

/**
 * Leaving a channel or closing a DM (removing yourself), optimistically: the
 * channel leaves the sidebar and you leave its member list at once.
 */
export function useRemoveChannelMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, memberId }: { channelId: string; memberId: string }) =>
      removeChannelMember(channelId, memberId),
    onMutate: async ({ channelId, memberId }) => {
      await queryClient.cancelQueries({ queryKey: channelKeys.list(false) });
      const previousList = queryClient.getQueryData<ChannelData[]>(channelKeys.list(false));
      queryClient.setQueryData<ChannelData[]>(channelKeys.list(false), (current) =>
        current?.filter((channel) => channel._id !== channelId)
      );
      const previousMembers = await patchMembers(queryClient, channelId, (members) =>
        members.filter((member) => memberUserId(member) !== memberId)
      );
      return { previousList, previousMembers };
    },
    onError: (_error, { channelId }, context) => {
      queryClient.setQueryData(channelKeys.list(false), context?.previousList);
      queryClient.setQueryData(channelKeys.members(channelId), context?.previousMembers);
    },
    onSettled: (_result, _error, { channelId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: channelKeys.list(false) }),
        queryClient.invalidateQueries({ queryKey: channelKeys.members(channelId) }),
      ]),
  });
}

/**
 * Optimistic member update. A notification level change (mute/unmute) is
 * only allowed on your own membership, so it patches your sidebar entry; a
 * role change patches the member list.
 */
export function useUpdateChannelMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      memberId,
      payload,
    }: {
      channelId: string;
      memberId: string;
      payload: { role?: 'moderator' | 'member'; notificationLevel?: 'all' | 'mentions' | 'muted' };
    }) => updateChannelMember(channelId, memberId, payload),
    onMutate: async ({ channelId, memberId, payload }) => {
      const { notificationLevel, role } = payload;
      const previousList = notificationLevel
        ? await patchActiveChannel(queryClient, channelId, (channel) => ({
            ...channel,
            notificationLevel,
          }))
        : undefined;
      const previousMembers = await patchMembers(queryClient, channelId, (members) =>
        members.map((member) =>
          memberUserId(member) === memberId
            ? {
                ...member,
                ...(role ? { role } : {}),
                ...(notificationLevel ? { notificationLevel } : {}),
              }
            : member
        )
      );
      return { previousList, previousMembers };
    },
    onError: (_error, { channelId }, context) => {
      if (context?.previousList)
        queryClient.setQueryData(channelKeys.list(false), context.previousList);
      queryClient.setQueryData(channelKeys.members(channelId), context?.previousMembers);
    },
    onSettled: (_result, _error, { channelId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: channelKeys.list(false) }),
        queryClient.invalidateQueries({ queryKey: channelKeys.members(channelId) }),
      ]),
  });
}

/**
 * Optimistic invite: the person appears in the member list at once (and so
 * drops out of the "Add members" candidates); a failure takes them out again.
 */
export function useAddChannelMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, person }: { channelId: string; person: ChatUser }) =>
      addChannelMember(channelId, person._id),
    onMutate: async ({ channelId, person }) => ({
      previous: await patchMembers(queryClient, channelId, (members) =>
        members.some((member) => memberUserId(member) === person._id)
          ? members
          : [
              ...members,
              {
                _id: `pending-${person._id}`,
                channelId,
                userId: person,
                role: 'member',
                notificationLevel: 'all',
              },
            ]
      ),
    }),
    onError: (_error, { channelId }, context) =>
      queryClient.setQueryData(channelKeys.members(channelId), context?.previous),
    onSettled: (_result, _error, { channelId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: channelKeys.members(channelId) }),
        // Member counts in the sidebar.
        queryClient.invalidateQueries({ queryKey: channelKeys.list(false) }),
      ]),
  });
}

const memberUserId = (member: ChannelMemberData) =>
  typeof member.userId === 'string' ? member.userId : member.userId._id;

/**
 * Optimistically rewrites one channel's member list; returns the previous
 * list for rollback. Used by the owner/moderator member management screen.
 */
async function patchMembers(
  queryClient: ReturnType<typeof useQueryClient>,
  channelId: string,
  update: (members: ChannelMemberData[]) => ChannelMemberData[]
): Promise<ChannelMemberData[] | undefined> {
  const key = channelKeys.members(channelId);
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<ChannelMemberData[]>(key);
  queryClient.setQueryData<ChannelMemberData[]>(key, (current) => current && update(current));
  return previous;
}

/** Owner only (enforced by the API): member ↔ moderator, applied optimistically. */
export function useChangeMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      memberId,
      role,
    }: {
      channelId: string;
      memberId: string;
      role: 'moderator' | 'member';
    }) => updateChannelMember(channelId, memberId, { role }),
    onMutate: async ({ channelId, memberId, role }) => ({
      previous: await patchMembers(queryClient, channelId, (members) =>
        members.map((member) => (memberUserId(member) === memberId ? { ...member, role } : member))
      ),
    }),
    onError: (_error, { channelId }, context) =>
      queryClient.setQueryData(channelKeys.members(channelId), context?.previous),
    onSettled: (_result, _error, { channelId }) =>
      queryClient.invalidateQueries({ queryKey: channelKeys.members(channelId) }),
  });
}

/** Removes someone else from a channel, optimistically; the API applies the rules. */
export function useRemoveMemberFromChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, memberId }: { channelId: string; memberId: string }) =>
      removeChannelMember(channelId, memberId),
    onMutate: async ({ channelId, memberId }) => ({
      previous: await patchMembers(queryClient, channelId, (members) =>
        members.filter((member) => memberUserId(member) !== memberId)
      ),
    }),
    onError: (_error, { channelId }, context) =>
      queryClient.setQueryData(channelKeys.members(channelId), context?.previous),
    onSettled: (_result, _error, { channelId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: channelKeys.members(channelId) }),
        // Member counts in the sidebar.
        queryClient.invalidateQueries({ queryKey: channelKeys.list(false) }),
      ]),
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      payload,
    }: {
      channelId: string;
      payload: Partial<
        Pick<
          ChannelData,
          'name' | 'description' | 'visibility' | 'isReadOnly' | 'messagePermission'
        >
      > & {
        archived?: boolean;
      };
    }) => updateChannel(channelId, payload),
    // Optimistic: the sidebar, header and details reflect new settings at once.
    onMutate: async ({ channelId, payload }) => {
      // Archiving has its own optimistic flow; only patch plain settings here.
      const settings = { ...payload };
      delete settings.archived;
      return {
        previous: await patchActiveChannel(queryClient, channelId, (channel) => ({
          ...channel,
          ...settings,
        })),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(channelKeys.list(false), context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: channelKeys.list(false) }),
  });
}

/** Refetches both sidebars; the archived list only loads while it is open. */
function refreshChannelLists(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: channelKeys.list(false) }),
    queryClient.invalidateQueries({ queryKey: channelKeys.list(true) }),
  ]);
}

interface ChannelListsSnapshot {
  active?: ChannelData[];
  archived?: ChannelData[];
}

/** Stops in-flight list fetches from overwriting an optimistic move. */
async function snapshotChannelLists(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<ChannelListsSnapshot> {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: channelKeys.list(false) }),
    queryClient.cancelQueries({ queryKey: channelKeys.list(true) }),
  ]);
  return {
    active: queryClient.getQueryData<ChannelData[]>(channelKeys.list(false)),
    archived: queryClient.getQueryData<ChannelData[]>(channelKeys.list(true)),
  };
}

function rollbackChannelLists(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: ChannelListsSnapshot | undefined
): void {
  if (!snapshot) return;
  queryClient.setQueryData(channelKeys.list(false), snapshot.active);
  queryClient.setQueryData(channelKeys.list(true), snapshot.archived);
}

/**
 * Optimistic: the channel leaves the active sidebar (and joins a loaded
 * archived list) immediately; a failure puts both lists back.
 */
export function useArchiveChannel() {
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  return useMutation({
    mutationFn: (channelId: string) => archiveChannel(channelId),
    onMutate: async (channelId) => {
      const snapshot = await snapshotChannelLists(queryClient);
      const channel = snapshot.active?.find((item) => item._id === channelId);
      queryClient.setQueryData<ChannelData[]>(channelKeys.list(false), (current) =>
        current?.filter((item) => item._id !== channelId)
      );
      if (channel && snapshot.archived) {
        const archiver: ChatUser | null = me
          ? {
              _id: me._id,
              firstName: me.firstName,
              lastName: me.lastName,
              email: me.email,
              avatar: me.avatar ?? undefined,
            }
          : null;
        queryClient.setQueryData<ChannelData[]>(channelKeys.list(true), [
          {
            ...channel,
            archivedAt: new Date().toISOString(),
            archivedBy: me?._id,
            archivedByUser: archiver,
          },
          ...snapshot.archived.filter((item) => item._id !== channelId),
        ]);
      }
      return snapshot;
    },
    onError: (_error, _channelId, snapshot) => rollbackChannelLists(queryClient, snapshot),
    onSettled: () => refreshChannelLists(queryClient),
  });
}

/**
 * Optimistic: the channel moves back to the active sidebar immediately. The
 * refetch afterwards brings the server's view, e.g. member counts after
 * people who left the channel's company were removed.
 */
export function useRestoreChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => restoreChannel(channelId),
    onMutate: async (channelId) => {
      const snapshot = await snapshotChannelLists(queryClient);
      const channel = snapshot.archived?.find((item) => item._id === channelId);
      queryClient.setQueryData<ChannelData[]>(channelKeys.list(true), (current) =>
        current?.filter((item) => item._id !== channelId)
      );
      if (channel && snapshot.active) {
        queryClient.setQueryData<ChannelData[]>(channelKeys.list(false), [
          ...snapshot.active.filter((item) => item._id !== channelId),
          {
            ...channel,
            archivedAt: undefined,
            archivedBy: undefined,
            archivedByUser: undefined,
          },
        ]);
      }
      return snapshot;
    },
    onError: (_error, _channelId, snapshot) => rollbackChannelLists(queryClient, snapshot),
    onSettled: () => refreshChannelLists(queryClient),
  });
}
